import { CollapseInfo, SpinResponse } from '@cgs/data-access';
import {
  CollapseGameStateMachine,
  CollapseReelsEngine,
  ComponentNames,
  ConditionIntervalAction,
  Entity,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameConfigProvider,
  IGameStateMachineProvider,
  IReelsEngineProvider,
  ISlotGameEngineProvider,
  IconAnimationHelper,
  IconEnumerator,
  IconEnumeratorComponent,
  PlaySoundAction,
  ReelsSoundModel,
  RegularSpinsSoundModelComponent,
  StopSoundAction,
  T_IGameConfigProvider,
  T_ISlotGameEngineProvider,
  T_IconEnumeratorComponent,
  T_RegularSpinsSoundModelComponent,
} from '@cgs/machines';
import {
  Container,
  EmptyAction,
  FunctionAction,
  IntervalAction,
  ParallelAction,
  SequenceAction,
  SequenceSimpleAction,
  Vector2,
} from '@cgs/wge';
import { CollapseGameConfig } from '../../../reels_engine/free_fall_reels_engine/game_config/collapse_game_config';

export const COLLAPSE_ANIMATION_COMPONENT_TOKEN = Symbol('CollapseAnimationComponent');

export const enum WinStrategy {
  WinLines,
  WinPositions,
}

export interface ICollapseAnimationComponentParams {
  collapseStateName?: string;
  collapseDurationOffset?: number;
  playBangSound?: boolean;
  bangSoundName?: string;
  playCollapseSound?: boolean;
  collapseSoundName?: string;
  collapseSpeed?: number;
  winStrategy?: WinStrategy;
}

export const DEFAULT_PARAMS: ICollapseAnimationComponentParams = {
  collapseStateName: 'collapse',
  collapseDurationOffset: 0,
  playBangSound: true,
  bangSoundName: 'bang',
  playCollapseSound: true,
  collapseSoundName: 'collapse',
  collapseSpeed: 60,
  winStrategy: WinStrategy.WinLines,
};

export class CollapseAnimationComponent {
  protected readonly gameConfig: CollapseGameConfig;
  protected readonly reelsEngine: CollapseReelsEngine;
  protected readonly iconAnimationHelper: IconAnimationHelper;
  protected readonly reelSoundModel: ReelsSoundModel;
  protected readonly container: Container;
  protected readonly params: ICollapseAnimationComponentParams;
  protected readonly gameStateMachine: CollapseGameStateMachine<SpinResponse>;
  private readonly iconEnumerator: IconEnumerator;

  constructor(container: Container, params: ICollapseAnimationComponentParams = {}) {
    this.container = container;
    this.params = { ...DEFAULT_PARAMS, ...params };

    this.gameStateMachine = this.container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine as CollapseGameStateMachine<SpinResponse>;

    this.gameConfig = this.container.resolve<IGameConfigProvider>(T_IGameConfigProvider)
      .gameConfig as CollapseGameConfig;

    this.iconEnumerator =
      this.container.resolve<IconEnumeratorComponent>(T_IconEnumeratorComponent).iconsEnumerator;

    this.reelsEngine = (
      this.container.resolve<ISlotGameEngineProvider>(
        T_ISlotGameEngineProvider
      ) as IReelsEngineProvider
    ).reelsEngine as CollapseReelsEngine;
    this.iconAnimationHelper = this.reelsEngine.iconAnimationHelper;
    this.reelSoundModel = this.container.resolve<RegularSpinsSoundModelComponent>(
      T_RegularSpinsSoundModelComponent
    ).regularSpinSoundModel;

    this.gameStateMachine.collapseState.appendLazyAnimation(
      () =>
        new SequenceSimpleAction([
          this.bangAction(this.gameStateMachine.curResponse),
          this.collapseAction(this.gameStateMachine.curResponse),
          new ConditionIntervalAction(() => this.reelsEngine.slotCollapsed()).withDuration(-1.0),
        ])
    );
  }

  protected bangAction(response: SpinResponse): IntervalAction {
    const bangActions: IntervalAction[] = [];
    const delayActions: IntervalAction[] = [];
    const hideActions: IntervalAction[] = [];

    delayActions.push(this.playBangSoundAction());

    let positions: number[] = [];
    if (this.params.winStrategy === WinStrategy.WinLines) {
      positions = this.flattenWinPositions(response.currentWinLines);
    } else if (this.params.winStrategy === WinStrategy.WinPositions) {
      positions = this.flattenWinPositions(response.currentWinPositions);
    }

    if (positions.length === 0) {
      return new EmptyAction();
    }

    for (const position of positions) {
      this.createExplodeAnimationAction(bangActions, position, delayActions, hideActions);
    }

    return new SequenceAction([
      new ParallelAction(bangActions),
      new ParallelAction(delayActions),
      new ParallelAction(hideActions),
    ]);
  }

  protected playBangSoundAction(): IntervalAction {
    if (!this.params.playBangSound || !this.params.bangSoundName) {
      return new EmptyAction();
    }

    const bangSound = this.reelSoundModel.getSoundByName(this.params.bangSoundName);
    return new SequenceAction([new StopSoundAction(bangSound), new PlaySoundAction(bangSound)]);
  }

  protected createExplodeAnimationAction(
    bangActions: IntervalAction[],
    position: number,
    delayActions: IntervalAction[],
    hideActions: IntervalAction[]
  ) {
    this.pushBangActions(bangActions, position);
    this.pushDelayActions(delayActions, position);
    this.pushHideIconsActions(hideActions, position);
  }

  protected pushBangActions(bangActions: IntervalAction[], position: number) {
    bangActions.push(
      new FunctionAction(
        () => this.iconAnimationHelper.startAnimOnIcon(position, this.params.collapseStateName!),
        false
      )
    );
  }

  protected pushDelayActions(delayActions: IntervalAction[], position: number): void {
    delayActions.push(
      new EmptyAction().withDuration(
        this.iconAnimationHelper.getMaxAnimDuration(position, this.params.collapseStateName!) +
          this.params.collapseDurationOffset!
      )
    );
  }

  protected pushHideIconsActions(hideActions: IntervalAction[], position: number): void {
    hideActions.push(
      new FunctionAction(() => {
        const line = this.iconAnimationHelper.getLineIndex(position);
        const reel = this.iconAnimationHelper.getReelIndex(position);

        let entities = this.reelsEngine.entityCacheHolder.getAnimationEntities(reel, line, false);
        entities.forEach((e) => this.reelsEngine.hideAnimEntity(e));

        entities = this.reelsEngine.entityCacheHolder.getSoundEntities(reel, line, false);
        entities.forEach((e) => this.reelsEngine.hideSoundEntity(e));

        entities = this.iconAnimationHelper.getEntities(position);
        const entitiesCopy: Entity[] = [];
        entities.forEach((e) => {
          entitiesCopy.push(e);
          this.iconAnimationHelper.stopAnimOnEntity(e, this.params.collapseStateName!);
        });

        for (const entity of entitiesCopy) {
          this.reelsEngine.entityCacheHolder.removeEntity(reel, line, entity);
        }
      })
    );
  }

  private collapseAction(response: SpinResponse): IntervalAction {
    const collapseInfo = response.collapseInfo;

    if (!collapseInfo) {
      return new EmptyAction();
    }

    if (this.params.winStrategy === WinStrategy.WinPositions) {
      return this.collapseWinAction(
        collapseInfo,
        this.flattenWinPositions(response.currentWinPositions)
      );
    } else {
      return this.collapseWinAction(
        collapseInfo,
        this.flattenWinPositions(response.currentWinLines)
      );
    }
  }

  protected collapseWinAction(collapseInfo: CollapseInfo, bangPositions: number[]): IntervalAction {
    const actions: IntervalAction[] = [];

    const fallAction = this.fallDownCurrentReels(bangPositions);
    const fallDownAction = new FunctionAction(() =>
      this.fallDownNewReels(
        collapseInfo.currentRound!.positions,
        collapseInfo.currentRound!.newReels
      )
    );

    actions.push(this.playCollapseSoundAction());

    actions.push(new SequenceAction([fallAction, fallDownAction]));
    actions.push(new FunctionAction(() => collapseInfo.nextRound()));
    actions.push(
      new EmptyAction().withDuration(
        this.gameConfig.regularSpinCollapsingConfig.collapsingParameters!.roundFallingStartDelay
      )
    );
    return new SequenceAction(actions);
  }

  protected playCollapseSoundAction(): IntervalAction {
    if (!this.params.playCollapseSound || !this.params.collapseSoundName) {
      return new EmptyAction();
    }

    const collapseSound = this.reelSoundModel.getSoundByName(this.params.collapseSoundName!);
    return collapseSound
      ? new SequenceAction([new StopSoundAction(collapseSound), new PlaySoundAction(collapseSound)])
      : new EmptyAction();
  }

  protected flattenWinPositions(winPositions: { positions: number[] }[]): number[] {
    const positions = new Set<number>();
    winPositions.forEach((wp) => wp.positions.forEach((i) => positions.add(i)));
    return [...positions];
  }

  protected fallDownNewReels(positions: number[], newReels: number[]): void {
    // Теперь для каждой позиции определим порядок иконки в риле
    const reelCounters: number[] = new Array(this.reelsEngine.ReelConfig.reelCount).fill(0);

    for (let i = this.reelsEngine.ReelConfig.lineCount - 1; i >= 0; i--) {
      for (let j = 0; j < this.reelsEngine.ReelConfig.reelCount; j++) {
        const position = i * this.reelsEngine.ReelConfig.reelCount + j;
        if (positions.includes(position)) {
          const drawIndex = newReels[positions.indexOf(position)];
          const reel = this.iconAnimationHelper.getReelIndex(position);
          const line = this.iconAnimationHelper.getLineIndex(position);

          // Номер иконки в этом риле (0 = нижняя, 1 = средняя, 2 = верхняя)
          const iconOrderInReel = reelCounters[reel]++;

          let entity =
            this.reelsEngine.hiddenAnimationEntities[reel][
              this.reelsEngine.hiddenAnimationEntities[reel].length - 1
            ];

          this.reelsEngine.hiddenAnimationEntities[reel] = this.reelsEngine.hiddenAnimationEntities[
            reel
          ].filter((e) => e !== entity);

          entity =
            this.reelsEngine.hiddenSoundEntities[reel][
              this.reelsEngine.hiddenSoundEntities[reel].length - 1
            ];

          this.reelsEngine.hiddenSoundEntities[reel] = this.reelsEngine.hiddenSoundEntities[
            reel
          ].filter((e) => e !== entity);

          if (!entity) {
            continue;
          }

          this.fallDownEntityFromTop(entity, reel, line, drawIndex, iconOrderInReel);
        }
      }
    }

    this.reelsEngine.initHiddenEntities();
  }

  protected fallDownCurrentReels(bangPositions: number[]): IntervalAction {
    const actions: IntervalAction[] = [new EmptyAction()];
    for (let i = 0; i < this.reelsEngine.internalConfig.reelCount; i++) {
      let fallCounter = 0;

      for (let j = this.reelsEngine.internalConfig.lineCount - 1; j >= 0; j--) {
        const reelIndex = i;
        const lineIndex = j;
        const position = lineIndex * this.reelsEngine.internalConfig.reelCount + reelIndex;

        if (bangPositions.includes(position)) {
          fallCounter++;
        } else if (fallCounter > 0) {
          const subActions: IntervalAction[] = [];

          const iconDelay =
            this.gameConfig.regularSpinCollapsingConfig.collapsingParameters!.iconFallingStartDelay;

          const entities = this.reelsEngine.entityCacheHolder.getAnimationEntities(
            reelIndex,
            lineIndex,
            false
          );

          const finalLine = lineIndex + fallCounter;

          subActions.push(
            new FunctionAction(() => {
              const entitiesCopy: Entity[] = [
                ...this.reelsEngine.entityCacheHolder.getAnimationEntities(
                  reelIndex,
                  lineIndex,
                  false
                ),
              ];

              for (const entity of entitiesCopy) {
                this.reelsEngine.entityCacheHolder.removeEntity(reelIndex, lineIndex, entity);
                this.reelsEngine.entityCacheHolder.replaceEntities(reelIndex, finalLine, [entity]);
              }
            })
          );

          if (iconDelay > 0) {
            entities.forEach((_entity, entityIndex) => {
              const delay = entityIndex * iconDelay;
              subActions.push(new EmptyAction().withDuration(delay));
            });
          }

          entities.forEach((entity) => {
            const drawIndex = entity.get<number>(
              this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.DrawableIndex)
            );
            const fallDistance = fallCounter;
            subActions.push(
              new FunctionAction(() => {
                this.fallDownEntity(entity, reelIndex, finalLine, drawIndex, fallDistance);
              })
            );
          });

          actions.push(new SequenceAction(subActions));
        }
      }
    }

    return new ParallelAction(actions);
  }

  private fallDownEntity(
    entity: Entity,
    reel: number,
    line: number,
    drawIndex: number,
    fallDistance: number = 1
  ): void {
    const adjustedSpeed = this.params.collapseSpeed! * fallDistance;
    const duration = this.gameConfig.regularSpinConfig.accelerationDuration;

    entity.set(
      this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.DrawableIndex),
      drawIndex
    );
    this.iconEnumerator.setWinIndex(reel, line, drawIndex);
    this.reelsEngine.entityCacheHolder.addEntities(reel, line, false, [entity]);

    this.reelsEngine.moveEntityTo(
      entity,
      line,
      new Vector2(0.0, adjustedSpeed),
      duration,
      this.reelsEngine.ReelConfig.getStopEasing(reel)
    );
  }

  private fallDownEntityFromTop(
    entity: Entity,
    reel: number,
    line: number,
    drawIndex: number,
    iconOrderInReel: number
  ): void {
    entity.set(
      this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.DrawableIndex),
      drawIndex
    );
    this.iconEnumerator.setWinIndex(reel, line, drawIndex);

    // Устанавливаем стартовую позицию выше экрана на iconOrderInReel иконок
    const symbolHeight = this.reelsEngine.ReelConfig.symbolSize.y;
    const extraOffset = iconOrderInReel * symbolHeight;

    // Получаем текущую позицию иконки и модифицируем Y координату
    const currentPosition = entity.get<Vector2>(
      this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.Position)
    );
    const startPosition = currentPosition.clone();
    startPosition.y -= extraOffset;

    entity.set(
      this.reelsEngine.entityEngine.getComponentIndex(ComponentNames.Position),
      startPosition
    );

    this.reelsEngine.entityCacheHolder.addEntities(reel, line, false, [entity]);

    // Теперь анимируем падение до финальной позиции с базовой скоростью
    this.reelsEngine.moveEntityTo(
      entity,
      line,
      new Vector2(0.0, this.params.collapseSpeed!),
      this.gameConfig.regularSpinConfig.accelerationDuration,
      this.reelsEngine.ReelConfig.getStopEasing(reel)
    );
  }
}
